import React, { useState } from 'react';
import { IntegrationSuite, TestResult } from '@/services/IntegrationSuite';
import { Play, CheckCircle2, AlertCircle, Loader2, RefreshCw, Smartphone, Shield, AlertTriangle } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

interface IntegrationTestPanelProps {
  darkMode: boolean;
  pools: {
    ballPool: any;
    trailPool: any;
    collisionPool: any;
    blastPool: any;
  };
  activeWorker: Worker | null;
}

export const IntegrationTestPanel: React.FC<IntegrationTestPanelProps> = ({
  darkMode,
  pools,
  activeWorker
}) => {
  const [results, setResults] = useState<TestResult[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [completedCount, setCompletedCount] = useState(0);

  const startVerificationSuite = async () => {
    if (isRunning) return;
    setIsRunning(true);
    setCompletedCount(0);

    try {
      await IntegrationSuite.runAllTests(pools, activeWorker, (updatedResults) => {
        setResults(updatedResults);
        const resolved = updatedResults.filter(r => r.status === 'PASSED' || r.status === 'FAILED').length;
        setCompletedCount(resolved);
      });
    } catch (err) {
      console.error('[IntegrationSuite] Execution failure:', err);
    } finally {
      setIsRunning(false);
    }
  };

  const hasRuns = results.length > 0;
  const passedAll = hasRuns && results.every(r => r.status === 'PASSED');
  const failedAny = hasRuns && results.some(r => r.status === 'FAILED');

  return (
    <div className={`p-5 rounded-2xl border ${
      darkMode 
        ? 'bg-zinc-950/40 border-white/5 text-zinc-100 shadow-black/40' 
        : 'bg-zinc-100/50 border-zinc-200 text-zinc-900 shadow-sm'
    }`}>
      {/* Header and trigger */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
        <div>
          <h4 className="text-[11px] font-black uppercase tracking-widest text-[#00f0ff] mb-1">
            Production Quality Verification Suite
          </h4>
          <p className="text-[9px] opacity-40 uppercase font-black leading-tight">
            Self-tests for Multi-threading, WebGL contexts, FM synthesized audio, and off-thread exporters.
          </p>
        </div>

        <button
          onClick={startVerificationSuite}
          disabled={isRunning}
          id="btn-run-all-integration-tests"
          className={`px-4 py-2.5 rounded-xl text-[9px] font-black uppercase tracking-wider flex items-center gap-2 transition-all active:scale-95 whitespace-nowrap self-start sm:self-auto ${
            isRunning
              ? 'bg-zinc-800 text-zinc-400 cursor-wait'
              : 'bg-gradient-to-r from-cyan-600 to-indigo-600 hover:from-cyan-500 hover:to-indigo-500 text-white shadow-lg shadow-cyan-500/10'
          }`}
        >
          {isRunning ? (
            <>
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Verifying System... ({completedCount}/6)
            </>
          ) : (
            <>
              <Play className="w-3.5 h-3.5" />
              Launch Integration Suite
            </>
          )}
        </button>
      </div>

      {/* Verification Results list */}
      <AnimatePresence mode="popLayout">
        {hasRuns && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="space-y-2 overflow-hidden mt-4"
          >
            {/* Master Summary Box */}
            <div className={`p-3 rounded-xl border text-[10px] leading-relaxed uppercase font-bold flex items-start gap-2.5 ${
              passedAll 
                ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
                : failedAny
                ? 'bg-rose-500/10 border-rose-500/20 text-rose-400'
                : 'bg-cyan-500/10 border-cyan-500/20 text-cyan-400'
            }`}>
              {passedAll ? (
                <>
                  <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-400" />
                  <div>
                    <div className="font-extrabold text-[10px]">ALL VERIFICATION PASSES CONFIRMED (100% OK)</div>
                    <div className="text-[8px] font-semibold opacity-70 mt-0.5">
                      Subsystems are certified fit for deployment on mobile devices (Retina, high-dpi), main browser engines, and Tauri bundle wrappers.
                    </div>
                  </div>
                </>
              ) : failedAny ? (
                <>
                  <AlertTriangle className="w-4 h-4 shrink-0 text-rose-400" />
                  <div>
                    <div className="font-extrabold text-[10px]">VERIFICATION SUITE DETECTED SUSPICIOUS LATENCY / CONTEXT ERRS</div>
                    <div className="text-[8px] font-semibold opacity-70 mt-0.5">
                      Double-check browser background thread states, GPU device focus, or active audio output device context configurations.
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <Loader2 className="w-4 h-4 shrink-0 text-cyan-400 animate-spin" />
                  <div>
                    <div className="font-extrabold text-[10px]">INTEGRATION SUITE ACTIVE AND BENCHMARKING PIPELINES...</div>
                    <div className="text-[8px] font-semibold opacity-70 mt-0.5">
                      Analyzing WebGL instances, FM synth voice allocation loops, and Worker channels under stress.
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Step Checklists */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
              {results.map((r, idx) => {
                const passed = r.status === 'PASSED';
                const failed = r.status === 'FAILED';
                const running = r.status === 'RUNNING';

                return (
                  <div
                    key={r.id}
                    className={`p-3 rounded-xl border flex flex-col justify-between gap-1 transition-all ${
                      running
                        ? 'border-cyan-500/30 bg-cyan-500/5 shadow-inner'
                        : passed
                        ? 'border-emerald-500/10 bg-emerald-500/5'
                        : failed
                        ? 'border-rose-500/15 bg-rose-500/5 animate-pulse'
                        : 'border-zinc-500/5 bg-transparent opacity-55'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex flex-col items-start text-left">
                        <span className={`text-[8px] px-1.5 py-0.5 rounded-full font-black uppercase tracking-wider ${
                          passed
                            ? 'bg-emerald-500/10 text-emerald-400'
                            : failed
                            ? 'bg-rose-500/10 text-rose-400'
                            : running
                            ? 'bg-cyan-500/10 text-cyan-400 animate-pulse'
                            : 'bg-zinc-500/10 text-zinc-500'
                        }`}>
                          {r.category}
                        </span>
                        <span className="text-[10px] font-bold mt-1.5 leading-tight">{r.name}</span>
                      </div>
                      
                      <div className="shrink-0 mt-0.5">
                        {passed && <CheckCircle2 className="w-4 h-4 text-emerald-400" />}
                        {failed && <AlertCircle className="w-4 h-4 text-rose-400" />}
                        {running && <Loader2 className="w-4 h-4 text-cyan-400 animate-spin" />}
                        {r.status === 'PENDING' && <div className="w-4 h-4 rounded-full border border-zinc-500/20 bg-zinc-500/10" />}
                      </div>
                    </div>

                    {r.meta && (
                      <div className="flex items-center justify-between text-[8px] uppercase mt-2 pt-1.5 border-t border-zinc-500/5">
                        <span className="opacity-45 leading-none">Diagnostic:</span>
                        <span className={`font-black tracking-tight leading-none ${
                          passed ? 'text-emerald-400' : failed ? 'text-rose-400' : 'text-cyan-400'
                        }`}>
                          {r.meta}
                        </span>
                      </div>
                    )}

                    {r.timeMs !== undefined && (
                      <div className="flex justify-end text-[7px] opacity-30 mt-0.5">
                        ELAPSED: {r.timeMs.toFixed(0)}MS
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
